"use client"

import * as React from "react"
import { cn } from "../../lib/utils"
import type { ChoiceInteraction } from "../../schema"
import { ChoiceIndicator } from "../../ui/choice-indicator"
import { Field, FieldContent, FieldGroup, FieldLabel, FieldSet, FieldTitle } from "../../ui/field"
import { RadioGroup } from "../../ui/radio-group"
import { HTMLContent } from "../shared/html-content"

// ============================================================================
// Types
// ============================================================================

export interface ChoiceInteractionRendererProps {
	/** The choice interaction to render */
	interaction: ChoiceInteraction
	/** Current user response for this interaction */
	response?: string | string[]
	/** Callback when user selects an answer */
	onAnswerSelect?: (value: string | string[]) => void
	/** Whether to disable the interaction */
	disabled?: boolean
	/** Whether submit occurred; used to lock inputs and color selections */
	hasSubmitted?: boolean
	/** Idle palette (before submit) */
	palette?: "default" | "betta" | "cardinal" | "bee" | "owl" | "macaw"
	/** Overall correctness (single-select) used for selected coloring */
	isCorrect?: boolean
	/** Per-selection correctness (multi-select) for secure highlighting */
	selectedChoicesCorrectness?: Array<{ id: string; isCorrect: boolean }>
}

// ============================================================================
// Choice Interaction Renderer
// ============================================================================

/**
 * Renders a choice interaction (single or multiple choice)
 */
export function ChoiceInteractionRenderer({
	interaction,
	response,
	onAnswerSelect,
	disabled = false,
	hasSubmitted,
	palette = "macaw",
	isCorrect,
	selectedChoicesCorrectness
}: ChoiceInteractionRendererProps) {
	// QTI 3.0: max-choices="1" is single-select; "0" means unlimited and any
	// other value >1 caps a multi-select. Only exactly 1 renders as radio.
	const isMultiple = interaction.maxChoices !== 1
	const selectedValues = React.useMemo(() => {
		if (!response) return []
		return Array.isArray(response) ? response : [response]
	}, [response])

	// Detect if all choices are image-only (contains <img> and no text once tags are stripped)
	const isImagesOnly = React.useMemo(() => {
		const hasImg = (html: string) => html.toLowerCase().includes("<img")
		const stripped = (html: string) => html.replace(/<[^>]*>/g, "").trim()
		return (
			interaction.choices.length > 0 &&
			interaction.choices.every((c) => hasImg(c.contentHtml) && stripped(c.contentHtml) === "")
		)
	}, [interaction.choices])

	const choiceCount = interaction.choices.length

	// Grid layout for image-only sets (using CSS classes from themes.css)
	const gridClass = React.useMemo(() => {
		if (!isImagesOnly) return "space-y-2"
		switch (choiceCount) {
			case 5:
				return "qti-image-grid-5"
			case 4:
				return "qti-image-grid-4"
			case 3:
				return "qti-image-grid-3"
			case 2:
				return "qti-image-grid-2"
			default:
				return "qti-image-grid-many"
		}
	}, [isImagesOnly, choiceCount])

	// Image sizing/centering helpers applied to the content wrapper
	const imageCardClass = React.useMemo(() => {
		if (!isImagesOnly) return ""
		return choiceCount === 2 ? "qti-image-card qti-image-card-large" : "qti-image-card"
	}, [isImagesOnly, choiceCount])

	const handleSingleChoice = (value: string) => {
		if (!disabled && onAnswerSelect) {
			onAnswerSelect(value)
		}
	}

	// Removed unused handler in favor of inline onCheckedChange logic

	// Render prompt if present
	const promptElement = interaction.promptHtml ? <HTMLContent html={interaction.promptHtml} className="mb-4" /> : null

	// Single choice (radio) with Field UI
	if (!isMultiple) {
		const submitted = hasSubmitted === true
		return (
			<div className="space-y-4">
				{promptElement}
				<RadioGroup
					value={selectedValues[0] || ""}
					onValueChange={(value) => handleSingleChoice(value)}
					disabled={disabled || submitted}
				>
					<FieldGroup>
						<FieldSet>
							{isImagesOnly ? (
								<div className={gridClass}>
									{interaction.choices.map((choice, index) => {
										const isSelected = selectedValues.includes(choice.identifier)
										const choicePalette = submitted && isSelected ? (isCorrect ? "owl" : "cardinal") : palette
										const domId = `${interaction.responseIdentifier}-${choice.identifier}`
										return (
											<FieldLabel key={choice.identifier} htmlFor={domId} palette={choicePalette}>
												<Field orientation="horizontal" data-disabled={disabled || submitted}>
													<ChoiceIndicator
														value={choice.identifier}
														id={domId}
														letter={String.fromCharCode(65 + index)}
														palette={choicePalette}
														disabled={disabled || submitted}
													/>
													<FieldContent className={cn(imageCardClass)}>
														<FieldTitle>
															<HTMLContent html={choice.contentHtml} />
														</FieldTitle>
													</FieldContent>
												</Field>
											</FieldLabel>
										)
									})}
								</div>
							) : (
								interaction.choices.map((choice, index) => {
									const isSelected = selectedValues.includes(choice.identifier)
									const choicePalette = submitted && isSelected ? (isCorrect ? "owl" : "cardinal") : palette
									const domId = `${interaction.responseIdentifier}-${choice.identifier}`
									return (
										<FieldLabel key={choice.identifier} htmlFor={domId} palette={choicePalette}>
											<Field orientation="horizontal" data-disabled={disabled || submitted}>
												<ChoiceIndicator
													value={choice.identifier}
													id={domId}
													aria-describedby={
														submitted && isSelected && choice.inlineFeedbackHtml ? `${domId}-fb` : undefined
													}
													letter={String.fromCharCode(65 + index)}
													palette={choicePalette}
													disabled={disabled || submitted}
												/>
												<FieldContent>
													<FieldTitle>
														<HTMLContent html={choice.contentHtml} />
													</FieldTitle>
													{submitted && isSelected && choice.inlineFeedbackHtml ? (
														<output
															id={`${domId}-fb`}
															aria-live="polite"
															className={cn(
																"mt-2 pl-3 border-l-4 text-sm text-muted-foreground",
																isCorrect ? "border-owl" : "border-cardinal"
															)}
														>
															<HTMLContent html={choice.inlineFeedbackHtml} />
														</output>
													) : null}
												</FieldContent>
											</Field>
										</FieldLabel>
									)
								})
							)}
						</FieldSet>
					</FieldGroup>
				</RadioGroup>
			</div>
		)
	}

	// Multi-select (checkbox) with Field UI
	const submitted = hasSubmitted === true
	// maxChoices 0 = unlimited (QTI 3.0), so the cap only applies when positive
	const atMax = interaction.maxChoices > 0 && selectedValues.length >= interaction.maxChoices
	return (
		<div className="space-y-4">
			{promptElement}
			<FieldGroup>
				<FieldSet>
					{isImagesOnly ? (
						<div className={gridClass}>
							{interaction.choices.map((choice, _index) => {
								const isChecked = selectedValues.includes(choice.identifier)
								const perSelection = selectedChoicesCorrectness?.find((c) => c.id === choice.identifier)?.isCorrect
								const selectedCorrect = perSelection ?? (isChecked ? (isCorrect ?? false) : false)
								const choicePalette = submitted && isChecked ? (selectedCorrect ? "owl" : "cardinal") : palette
								const domId = `${interaction.responseIdentifier}-${choice.identifier}`
								return (
									<FieldLabel key={choice.identifier} htmlFor={domId} palette={choicePalette}>
										<Field orientation="horizontal" data-disabled={disabled || submitted}>
											<ChoiceIndicator
												type="checkbox"
												id={domId}
												showLetter={false}
												palette={choicePalette}
												checked={isChecked}
												onCheckedChange={(checked) => {
													if (disabled || submitted || !onAnswerSelect) return
													// Enforce max choices: do not allow selecting new choices beyond the limit
													if (checked === true && !isChecked && atMax) return
													const next =
														checked === true
															? [...selectedValues, choice.identifier]
															: selectedValues.filter((v) => v !== choice.identifier)
													onAnswerSelect(next)
												}}
												disabled={disabled || submitted || (!isChecked && atMax)}
												aria-describedby={
													submitted && isChecked && choice.inlineFeedbackHtml ? `${domId}-fb` : undefined
												}
											/>
											<FieldContent className={cn(imageCardClass)}>
												<FieldTitle>
													<HTMLContent html={choice.contentHtml} />
												</FieldTitle>
												{submitted && isChecked && choice.inlineFeedbackHtml ? (
													<output
														id={`${domId}-fb`}
														aria-live="polite"
														className={cn(
															"mt-2 pl-3 border-l-4 text-sm text-muted-foreground",
															selectedCorrect ? "border-owl" : "border-cardinal"
														)}
													>
														<HTMLContent html={choice.inlineFeedbackHtml} />
													</output>
												) : null}
											</FieldContent>
										</Field>
									</FieldLabel>
								)
							})}
						</div>
					) : (
						interaction.choices.map((choice, _index) => {
							const isChecked = selectedValues.includes(choice.identifier)
							const perSelection = selectedChoicesCorrectness?.find((c) => c.id === choice.identifier)?.isCorrect
							const selectedCorrect = perSelection ?? (isChecked ? (isCorrect ?? false) : false)
							const choicePalette = submitted && isChecked ? (selectedCorrect ? "owl" : "cardinal") : palette
							const domId = `${interaction.responseIdentifier}-${choice.identifier}`
							return (
								<FieldLabel key={choice.identifier} htmlFor={domId} palette={choicePalette}>
									<Field orientation="horizontal" data-disabled={disabled || submitted}>
										<ChoiceIndicator
											type="checkbox"
											id={domId}
											showLetter={false}
											palette={choicePalette}
											checked={isChecked}
											onCheckedChange={(checked) => {
												if (disabled || submitted || !onAnswerSelect) return
												// Enforce max choices: do not allow selecting new choices beyond the limit
												if (checked === true && !isChecked && atMax) return
												const next =
													checked === true
														? [...selectedValues, choice.identifier]
														: selectedValues.filter((v) => v !== choice.identifier)
												onAnswerSelect(next)
											}}
											disabled={disabled || submitted || (!isChecked && atMax)}
											aria-describedby={submitted && isChecked && choice.inlineFeedbackHtml ? `${domId}-fb` : undefined}
										/>
										<FieldContent>
											<FieldTitle>
												<HTMLContent html={choice.contentHtml} />
											</FieldTitle>
											{submitted && isChecked && choice.inlineFeedbackHtml ? (
												<output
													id={`${domId}-fb`}
													aria-live="polite"
													className={cn(
														"mt-2 pl-3 border-l-4 text-sm text-muted-foreground",
														selectedCorrect ? "border-owl" : "border-cardinal"
													)}
												>
													<HTMLContent html={choice.inlineFeedbackHtml} />
												</output>
											) : null}
										</FieldContent>
									</Field>
								</FieldLabel>
							)
						})
					)}
				</FieldSet>
			</FieldGroup>
		</div>
	)
}

export default ChoiceInteractionRenderer
